export const CURRENCY_LOCALES: Record<string, string> = {
  AUD: "en-AU",
  USD: "en-US",
  EUR: "en-IE",
  GBP: "en-GB",
  ZAR: "en-ZA",
  NZD: "en-NZ",
  CAD: "en-CA",
  JPY: "ja-JP",
  INR: "en-IN",
  CHF: "de-CH",
  SGD: "en-SG",
};

export const CURRENCY_SYMBOLS: Record<string, string> = {
  AUD: "$",
  USD: "$",
  EUR: "€",
  GBP: "£",
  ZAR: "R",
  NZD: "$",
  CAD: "$",
  JPY: "¥",
  INR: "₹",
  CHF: "CHF",
  SGD: "$",
};

/** Closed set. Keep in step with orgs_local / user_preferences check constraints. */
export const DATE_FORMAT_OPTIONS = [
  { value: "MDY", label: "MM/DD/YYYY (10/25/2026)" },
  { value: "DMY", label: "DD/MM/YYYY (25/10/2026)" },
  { value: "YMD", label: "YYYY-MM-DD (2026-10-25)" },
  { value: "YMD_SLASH", label: "YYYY/MM/DD (2026/10/25)" },
  { value: "DMY_DOT", label: "DD.MM.YYYY (25.10.2026)" },
  { value: "MMM_DD_YYYY", label: "MMM DD, YYYY (Oct 25, 2026)" },
  { value: "DD_MMM_YYYY", label: "DD MMM YYYY (25 Oct 2026)" },
] as const;

export const TIME_FORMAT_OPTIONS = [
  { value: "12", label: "12-hour (3:40 PM)" },
  { value: "24", label: "24-hour (15:40)" },
] as const;

export const NUMBER_FORMAT_OPTIONS = [
  { value: "comma_dot", label: "1,234.56" },
  { value: "dot_comma", label: "1.234,56" },
  { value: "space_comma", label: "1 234,56" },
  { value: "space_dot", label: "1 234.56" },
  { value: "apostrophe_dot", label: "1'234.56" },
  { value: "lakh_dot", label: "12,34,567.89" },
] as const;

export type DateFormat = (typeof DATE_FORMAT_OPTIONS)[number]["value"];
export type TimeFormat = (typeof TIME_FORMAT_OPTIONS)[number]["value"];
export type NumberFormat = (typeof NUMBER_FORMAT_OPTIONS)[number]["value"];

const DATE_CODES = new Set<string>(DATE_FORMAT_OPTIONS.map((item) => item.value));
const TIME_CODES = new Set<string>(TIME_FORMAT_OPTIONS.map((item) => item.value));
const NUMBER_CODES = new Set<string>(NUMBER_FORMAT_OPTIONS.map((item) => item.value));

const NUMBER_SEPARATORS: Record<NumberFormat, { group: string; decimal: string }> = {
  comma_dot: { group: ",", decimal: "." },
  dot_comma: { group: ".", decimal: "," },
  space_comma: { group: " ", decimal: "," },
  space_dot: { group: " ", decimal: "." },
  apostrophe_dot: { group: "'", decimal: "." },
  lakh_dot: { group: ",", decimal: "." },
};

export interface LocaleSnapshot {
  timezone: string;
  currency: string;
  currencySymbol: string;
  locale: string;
  dateFormat: DateFormat;
  timeFormat: TimeFormat;
  numberFormat: NumberFormat;
  quoteApprovalRequired: boolean;
  orgTimezone: string;
  orgCurrency: string;
  orgCurrencySymbol: string;
  useOrgLocale: boolean;
  fiscalYearStartMonth: number;
  historyAfterDays: number;
}

export const FALLBACK_LOCALE: LocaleSnapshot = {
  timezone: "UTC",
  currency: "USD",
  currencySymbol: "$",
  locale: "en-US",
  dateFormat: "MDY",
  timeFormat: "12",
  numberFormat: "comma_dot",
  quoteApprovalRequired: false,
  orgTimezone: "UTC",
  orgCurrency: "AUD",
  orgCurrencySymbol: "$",
  useOrgLocale: true,
  fiscalYearStartMonth: 1,
  historyAfterDays: 1,
};

export function supportedTimeZones(): string[] {
  try {
    return Intl.supportedValuesOf("timeZone");
  } catch {
    return ["UTC", "Australia/Brisbane", "Australia/Sydney", "Africa/Johannesburg", "America/New_York", "Europe/London"];
  }
}

export function localeForCurrency(currency: string | null | undefined): string {
  return CURRENCY_LOCALES[(currency || "").toUpperCase()] ?? "en-US";
}

export function symbolForCurrency(currency: string | null | undefined): string {
  return CURRENCY_SYMBOLS[(currency || "").toUpperCase()] ?? "$";
}

/** What an unset format looks like for a currency. Matches the org migration backfill. */
export function formatsForCurrency(currency: string | null | undefined): {
  dateFormat: DateFormat;
  timeFormat: TimeFormat;
  numberFormat: NumberFormat;
} {
  switch ((currency || "").toUpperCase()) {
    case "USD":
      return { dateFormat: "MDY", timeFormat: "12", numberFormat: "comma_dot" };
    case "EUR":
    case "GBP":
      return { dateFormat: "DMY", timeFormat: "24", numberFormat: "comma_dot" };
    case "CHF":
      return { dateFormat: "DMY_DOT", timeFormat: "24", numberFormat: "apostrophe_dot" };
    case "CAD":
      return { dateFormat: "YMD", timeFormat: "12", numberFormat: "comma_dot" };
    case "JPY":
    case "ZAR":
      return { dateFormat: "YMD_SLASH", timeFormat: "24", numberFormat: "comma_dot" };
    case "INR":
      return { dateFormat: "DMY", timeFormat: "12", numberFormat: "lakh_dot" };
    default:
      return { dateFormat: "DMY", timeFormat: "12", numberFormat: "comma_dot" };
  }
}

export function asDateFormat(value: string | null | undefined, currency?: string | null): DateFormat {
  if (value && DATE_CODES.has(value)) return value as DateFormat;
  return formatsForCurrency(currency).dateFormat;
}

export function asTimeFormat(value: string | null | undefined, currency?: string | null): TimeFormat {
  if (value && TIME_CODES.has(value)) return value as TimeFormat;
  return formatsForCurrency(currency).timeFormat;
}

export function asNumberFormat(value: string | null | undefined, currency?: string | null): NumberFormat {
  if (value && NUMBER_CODES.has(value)) return value as NumberFormat;
  return formatsForCurrency(currency).numberFormat;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function formatCalendarDate(year: number, month: number, day: number, dateFormat: DateFormat): string {
  const y = String(year);
  const m = String(month).padStart(2, "0");
  const d = String(day).padStart(2, "0");
  const monthName = MONTHS[month - 1] ?? m;
  if (dateFormat === "MDY") return `${m}/${d}/${y}`;
  if (dateFormat === "YMD") return `${y}-${m}-${d}`;
  if (dateFormat === "YMD_SLASH") return `${y}/${m}/${d}`;
  if (dateFormat === "DMY_DOT") return `${d}.${m}.${y}`;
  if (dateFormat === "MMM_DD_YYYY") return `${monthName} ${d}, ${y}`;
  if (dateFormat === "DD_MMM_YYYY") return `${d} ${monthName} ${y}`;
  return `${d}/${m}/${y}`;
}

export function formatClock(hour24: number, minute: number, timeFormat: TimeFormat): string {
  const minutes = String(minute).padStart(2, "0");
  if (timeFormat === "24") return `${String(hour24).padStart(2, "0")}:${minutes}`;
  const period = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 || 12;
  return `${hour12}:${minutes} ${period}`;
}

function groupDigits(intRaw: string, format: NumberFormat): string {
  const group = NUMBER_SEPARATORS[format].group;
  if (format === "lakh_dot") {
    if (intRaw.length <= 3) return intRaw;
    const head = intRaw.slice(0, -3);
    const tail = intRaw.slice(-3);
    return `${head.replace(/\B(?=(\d{2})+(?!\d))/g, group)}${group}${tail}`;
  }
  return intRaw.replace(/\B(?=(\d{3})+(?!\d))/g, group);
}

export function formatNumberValue(
  value: number | string | null | undefined,
  options: { numberFormat?: NumberFormat; fractionDigits?: number } = {},
): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  const format = options.numberFormat ?? "comma_dot";
  const digits = options.fractionDigits ?? 0;
  const negative = n < 0;
  const [intRaw, frac] = Math.abs(n).toFixed(digits).split(".");
  const grouped = groupDigits(intRaw, format);
  const body = digits > 0 ? `${grouped}${NUMBER_SEPARATORS[format].decimal}${frac}` : grouped;
  return negative ? `-${body}` : body;
}

export function formatPreview(options: {
  dateFormat: DateFormat;
  timeFormat: TimeFormat;
  numberFormat: NumberFormat;
}): string {
  const date = formatCalendarDate(2026, 10, 25, options.dateFormat);
  const time = formatClock(15, 40, options.timeFormat);
  const amount = formatNumberValue(1234.56, { numberFormat: options.numberFormat, fractionDigits: 2 });
  return `${date}, ${time} · ${amount}`;
}

export function formatMoneyValue(
  value: number | string | null | undefined,
  options: {
    currency?: string | null;
    locale?: string;
    symbol?: string;
    compact?: boolean;
    numberFormat?: NumberFormat;
  } = {},
): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  const currency = (options.currency || "USD").toUpperCase();
  const digits = options.compact ? 0 : 2;
  const rounded = options.compact ? Math.round(n) : n;
  if (options.numberFormat) {
    const symbol = options.symbol || symbolForCurrency(currency);
    const body = formatNumberValue(Math.abs(rounded), { numberFormat: options.numberFormat, fractionDigits: digits });
    return rounded < 0 ? `-${symbol}${body}` : `${symbol}${body}`;
  }
  const locale = options.locale || localeForCurrency(currency);
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(rounded);
  } catch {
    const symbol = options.symbol || "$";
    return `${symbol}${rounded.toLocaleString(locale, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    })}`;
  }
}

function zonedParts(date: Date, timezone: string, hour12: boolean): Intl.DateTimeFormatPart[] {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: hour12 ? "h12" : "h23",
  }).formatToParts(date);
}

export interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

/** What a clock on the wall in `timezone` reads at this instant. */
export function zonedWallClock(date: Date, timezone: string): WallClock {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = zonedParts(date, timezone, false);
  } catch {
    parts = zonedParts(date, "UTC", false);
  }
  const pick = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0);
  return { year: pick("year"), month: pick("month"), day: pick("day"), hour: pick("hour") % 24, minute: pick("minute") };
}

/** Today's calendar date (YYYY-MM-DD) in `timezone`. `toISOString().slice(0, 10)` is the UTC day instead. */
export function todayInZone(timezone: string, now: Date = new Date()): string {
  const wall = zonedWallClock(now, timezone);
  return `${wall.year}-${String(wall.month).padStart(2, "0")}-${String(wall.day).padStart(2, "0")}`;
}

/** The instant at which the wall clock in `timezone` reads this time. */
export function zonedToInstant(wall: WallClock, timezone: string): Date {
  const target = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute);
  const offsetAt = (instant: number) => {
    const seen = zonedWallClock(new Date(instant), timezone);
    return Date.UTC(seen.year, seen.month - 1, seen.day, seen.hour, seen.minute) - instant;
  };
  const first = target - offsetAt(target);
  // A second pass lands on the right side of a daylight-saving change.
  return new Date(target - offsetAt(first));
}

/** A timestamp with no offset is a UTC instant. `new Date` would otherwise read it as local and show midnight. */
export function parseApiInstant(value: string | Date): Date {
  if (value instanceof Date) return value;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/.test(value)) return new Date(`${value}Z`);
  return new Date(value);
}

function formatZonedDateTime(date: Date, timezone: string, dateFormat: DateFormat, timeFormat: TimeFormat): string {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = zonedParts(date, timezone, timeFormat === "12");
  } catch {
    parts = zonedParts(date, "UTC", timeFormat === "12");
  }
  const pick = (type: string) => parts.find((part) => part.type === type)?.value ?? "";
  const year = Number(pick("year"));
  const month = Number(pick("month"));
  const day = Number(pick("day"));
  if (!year || !month || !day) return date.toISOString();
  const dateText = formatCalendarDate(year, month, day, dateFormat);
  if (timeFormat === "24") {
    return `${dateText}, ${pick("hour").padStart(2, "0")}:${pick("minute").padStart(2, "0")}`;
  }
  const hour = String(Number(pick("hour")));
  const period = pick("dayPeriod").replace(/\./g, "").toUpperCase().startsWith("A") ? "AM" : "PM";
  return `${dateText}, ${hour}:${pick("minute").padStart(2, "0")} ${period}`;
}

export function formatDateTimeValue(
  value: string | Date | null | undefined,
  options: {
    timezone?: string;
    locale?: string;
    dateOnly?: boolean;
    dateFormat?: DateFormat;
    timeFormat?: TimeFormat;
  } = {},
): string {
  if (value == null || value === "") return "—";
  const raw = typeof value === "string" ? value : value.toISOString();
  const timezone = options.timezone || "UTC";
  const locale = options.locale || "en-US";
  // A bare calendar date has no instant, so it is shown as-is in every timezone.
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    if (!year || !month || !day) return raw;
    if (options.dateFormat) return formatCalendarDate(year, month, day, options.dateFormat);
    return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(locale, { timeZone: "UTC" });
  }
  const date = parseApiInstant(value instanceof Date ? value : raw);
  if (Number.isNaN(date.getTime())) return String(value);
  // A timestamp shown as a date is the day it was in the viewer's timezone, not the UTC day.
  if (options.dateOnly) {
    const wall = zonedWallClock(date, timezone);
    if (options.dateFormat) return formatCalendarDate(wall.year, wall.month, wall.day, options.dateFormat);
    return date.toLocaleDateString(locale, { timeZone: timezone });
  }
  if (options.dateFormat) {
    return formatZonedDateTime(date, timezone, options.dateFormat, options.timeFormat ?? "24");
  }
  return date.toLocaleString(locale, { timeZone: timezone });
}

/** True once a finished record is at least `days` old and belongs in the History table. */
export function isAgedToHistory(finishedAt: string | null | undefined, days: number, now: number = Date.now()): boolean {
  if (!finishedAt) return false;
  const finished = new Date(finishedAt).getTime();
  if (Number.isNaN(finished)) return false;
  return now - finished >= days * 24 * 60 * 60 * 1000;
}

export function quoteWorkflow(
  status: string | null | undefined,
  approvalRequired: boolean,
  hasOrder = false,
  total?: number | string | null,
) {
  const draft = status === "draft" || status === "rejected";
  const pending = status === "pending_approval";
  const approved = status === "approved";
  const sent = status === "sent";
  const accepted = status === "accepted";
  // Only a lead may sit at zero. A quote with no total cannot move on. Omitted total = not known here.
  const priced = total === undefined || Number(total) > 0;
  return {
    canSubmit: priced && approvalRequired && draft,
    // Left open so a zero quote already waiting can still be rejected; the server refuses approval.
    canApprove: approvalRequired && pending,
    canSend: priced && (approved || (!approvalRequired && draft)),
    canAccept: priced && (sent || approved || (!approvalRequired && draft)),
    canGenerateOrder: priced && accepted && !hasOrder,
    canRevise: pending || approved || sent || status === "rejected",
  };
}
